import React, { useState, useEffect } from "react";
import {
    Form,
    Input,
    Button,
    Select,
    Row,
    Col,
    Switch,
    InputNumber,
} from "antd";
import { showToast } from "@/ts/modals";
import {
    IClientJob,
    IClientListItem,
    ClientJobTableColumns,
    IChannelReport,
    ClientJobFormFields,
    IChannels,
} from "@/ts/common-types";
import {
    getAvailableChannels,
    createClientJob,
    getChannelReports,
} from "@/ts/retrieve-data";
import { formatCronString } from "@/ts/utils";
import { PRIORITIES } from "@/ts/constants";
import * as Sentry from "@sentry/react";
import "@/scss/_channel-icons.scss";
import ChannelSelect from "@/components/drop-downs/ChannelSelect";

interface ClientJobFormProps {
    client: IClientListItem;
    editData?: IClientJob | null;
    onSuccess: () => void;
    onCancel: () => void;
}

const DEFAULT_JOB_FORM_VALUES = {
    [ClientJobFormFields.STATUS]: true,
    [ClientJobFormFields.JOB_PRIORITY]: "low",
    [ClientJobFormFields.PERIOD]: 30,
};

const ClientJobForm: React.FC<ClientJobFormProps> = ({
    client,
    editData,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [channels, setChannels] = useState<IChannels[]>([]);
    const [reports, setReports] = useState<IChannelReport[]>([]);
    const [cronPreview, setCronPreview] = useState<string>("");

    useEffect(() => {
        const loadChannels = async () => {
            try {
                const availableChannels = await getAvailableChannels(
                    client.country
                );
                setChannels(availableChannels);
            } catch (error) {
                console.error(error);
                showToast("danger", "Failed to load channels");
                Sentry.captureException(error);
            }
        };

        loadChannels();
    }, [client.country]);

    const handleCronStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cronString = e.target.value;
        const preview = formatCronString(cronString);
        setCronPreview(preview);
    };

    const handleChannelChange = async (channelId: number) => {
        try {
            const availableReports = await getChannelReports(channelId);
            setReports(availableReports);
            form.setFieldValue(ClientJobFormFields.REPORT_NAME, undefined);
        } catch (error) {
            console.error(error);
            showToast("danger", "Failed to load reports");
            Sentry.captureException(error);
        }
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const submitData = {
                ...values,
                job_priority: values.job_priority || "low",
            };
            await createClientJob(client.id, submitData);
            showToast("success", "Job created successfully");
            onSuccess();
        } catch (error: any) {
            console.error(error);
            showToast("danger", error.message || "Failed to create job");
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Row className="">
                <Col span={12}>
                    <h5>{editData ? "Edit Job" : "Create Job"}</h5>
                </Col>
                <Col span={12} className="text-end">
                    <Button type="primary" className="ms-3" onClick={onCancel}>
                        Manage Jobs
                    </Button>
                </Col>
            </Row>

            <div className="mt-4 overflow-y-auto overflow-x-hidden vh-75">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={editData || DEFAULT_JOB_FORM_VALUES}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.CHANNEL}
                                label={ClientJobTableColumns.CHANNEL}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select channel",
                                    },
                                ]}
                            >
                                <ChannelSelect
                                    channels={channels}
                                    onChange={handleChannelChange}
                                    placeholder="Select a channel"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.REPORT_NAME}
                                label={ClientJobTableColumns.REPORT_NAME}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a report",
                                    },
                                ]}
                            >
                                <Select
                                    disabled={
                                        !form.getFieldValue(
                                            ClientJobFormFields.CHANNEL
                                        )
                                    }
                                    placeholder="Select a report"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            ?.toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    optionFilterProp="children"
                                >
                                    {reports
                                        .filter(
                                            (report) =>
                                                report.sys_report_id != null
                                        )
                                        .map((report) => (
                                            <Select.Option
                                                key={`report-${report.sys_report_id}`}
                                                value={report.sys_report_id}
                                            >
                                                {report.report_name}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.PERIOD}
                                label={ClientJobTableColumns.PERIOD}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter period",
                                    },
                                ]}
                            >
                                <InputNumber
                                    className="w-100"
                                    min={1}
                                    max={365}
                                    placeholder="Enter number of days"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.JOB_PRIORITY}
                                label={ClientJobTableColumns.PRIORITY}
                            >
                                <Select
                                    options={PRIORITIES}
                                    placeholder="Select priority (defaults to Low)"
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.CRON_STRING}
                                label={ClientJobTableColumns.SCHEDULE}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter cron string",
                                    },
                                ]}
                                help={
                                    cronPreview ||
                                    "Enter a cron string to see its meaning"
                                }
                            >
                                <Input
                                    placeholder="0 1/3 * * *"
                                    className="border"
                                    onChange={handleCronStringChange}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={ClientJobFormFields.STATUS}
                                label={ClientJobTableColumns.STATUS}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item className="mt-4">
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            {editData ? "Update" : "Create"} Job
                        </Button>
                        <Button className="ms-2" onClick={onCancel}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </>
    );
};

export default ClientJobForm;
